import {
  InMemoryCache,
  PossibleTypesMap,
  TypePolicies,
  IdGetter,
} from '@apollo/client'

export interface CacheConfig {
  possibleTypes?: PossibleTypesMap
  typePolicies?: TypePolicies
  dataIdFromObject?: IdGetter
}

const dataIdFromObjectWithSanityCheck = (
  dataIdFromObject: IdGetter,
): IdGetter => (object) => {
  const result = dataIdFromObject(object)
  if (result && (result.includes('undefined') || result.endsWith('null'))) {
    // When a property is `undefined` it will be serialized to the string
    // "undefined". This makes this sanity check really easy to make.
    // An alternative would be to check for the `undefined` value in every
    // single prop, but that would make this much harder and more annoying
    // to write while effectively being no different.
    throw new Error(
      `Received an invalid dataId for type "${object.__typename}": \`${result}\`. An Apollo object dataId should never include the string "undefined". You likely forgot to add a required property to your query (e.g. \`id\`). Cacheable objects must always have an \`id\` field.`,
    )
  }
  return result
}

export default function createCache({
  possibleTypes,
  typePolicies = {},
  dataIdFromObject,
}: CacheConfig) {
  return new InMemoryCache({
    possibleTypes,
    dataIdFromObject:
      process.env.NODE_ENV === 'development' && dataIdFromObject
        ? dataIdFromObjectWithSanityCheck(dataIdFromObject)
        : dataIdFromObject,
    typePolicies: {
      ...typePolicies,
      Query: {
        ...(typePolicies.Query ?? {}),
        fields: {
          product(_, { args, toReference }) {
            return toReference({
              __typename: 'Product',
              id: args?.id,
            })
          },
          ...(typePolicies.Query?.fields ?? {}),
        },
      },
    },
  })
}
